<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { MediaVideoProps } from '@/types/components/MediaVideo';
import AdaptiveMedia from '@/elements/AdaptiveMedia.vue';
import utils from '@/utils';

const props = defineProps<MediaVideoProps>();

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-media-video'));

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});

const sectionEl = ref<HTMLElement | null>(null);
const maskLeftEl = ref<HTMLElement | null>(null);
const maskRightEl = ref<HTMLElement | null>(null);
const contentLeftEl = ref<HTMLElement | null>(null);
const contentRightEl = ref<HTMLElement | null>(null);

let triggers: ScrollTrigger[] = [];

const titleField = computed(() => props.fields?.title);
const descField = computed(() => props.fields?.description);
const mediaField = computed(() => props.fields?.media);

const buildAnimation = () => {
  if (
    !sectionEl.value ||
    !maskLeftEl.value ||
    !maskRightEl.value ||
    !contentLeftEl.value ||
    !contentRightEl.value
  ) {
    return;
  }

  triggers.forEach((t) => t.kill());
  triggers = [];

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sectionEl.value,
      start: 'top top',
      end: '+=100%',
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      scrub: 1,
    },
  });

  // 左遮罩：平行四边形向左移出，右边缘斜率 -0.2 (60%→40%)
  tl.fromTo(
    maskLeftEl.value,
    { clipPath: 'polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%)' },
    { clipPath: 'polygon(-50% 0%, 0% 0%, 0% 100%, -50% 100%)', ease: 'none' },
    0
  );

  // 右遮罩：与左遮罩边缘微量重叠，避免 clip-path 抗锯齿产生白缝
  tl.fromTo(
    maskRightEl.value,
    { clipPath: 'polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%)' },
    { clipPath: 'polygon(100% 0%, 150% 0%, 150% 100%, 100% 100%)', ease: 'none' },
    0
  );

  // 文字同步裁切
  tl.fromTo(
    contentLeftEl.value,
    { clipPath: 'polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%)' },
    { clipPath: 'polygon(-50% 0%, 0% 0%, 0% 100%, -50% 100%)', ease: 'none' },
    0
  );
  tl.fromTo(
    contentRightEl.value,
    { clipPath: 'polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%)' },
    { clipPath: 'polygon(100% 0%, 150% 0%, 150% 100%, 100% 100%)', ease: 'none' },
    0
  );

  if (tl.scrollTrigger) {
    triggers.push(tl.scrollTrigger);
  }
};

onMounted(() => {
  buildAnimation();
  window.addEventListener('resize', buildAnimation);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', buildAnimation);
  triggers.forEach((t) => t.kill());
  triggers = [];
});
</script>

<template>
  <section
    :class="rootClass"
    :style="rootStyle"
    ref="sectionEl"
    v-if="fields"
    :id="rendering?.dataSource"
    data-no-reveal
  >
    <AdaptiveMedia
      v-if="mediaField"
      class="c-media-video__video"
      :field="mediaField"
      :autoplay="true"
      :loop="true"
      :options="{ preload: 'auto' }"
      :show-image-redius="false"
      is-background
    />

    <div class="c-media-video__mask c-media-video__mask--left" ref="maskLeftEl"></div>
    <div class="c-media-video__mask c-media-video__mask--right" ref="maskRightEl"></div>

    <div class="c-media-video__content c-media-video__content--left" ref="contentLeftEl">
      <h2 v-if="titleField">
        <RichText class="c-media-video__title" :field="titleField" />
      </h2>
      <div class="c-media-video__divider"></div>
      <RichText v-if="descField" class="c-media-video__desc" :field="descField" />
    </div>
    <div class="c-media-video__content c-media-video__content--right" ref="contentRightEl">
      <h2 v-if="titleField">
        <RichText class="c-media-video__title" :field="titleField" />
      </h2>
      <div class="c-media-video__divider"></div>
      <RichText v-if="descField" class="c-media-video__desc" :field="descField" />
    </div>
  </section>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-media-video {
  position: relative;
  z-index: 2;
  isolation: isolate;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
  background: $black;
  padding: 0;

  &__video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    z-index: 0;

    &.e-adaptive-media,
    .e-adaptive-media__video,
    .e-video-player,
    video {
      width: 100%;
      height: 100% !important;
    }

    .e-video-player video,
    video {
      object-fit: contain !important;
      object-position: center;
    }
  }

  &__mask {
    position: absolute;
    inset: 0;
    background: $white;
    will-change: clip-path;
    z-index: 1;

    &--left {
      clip-path: polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%);
    }

    &--right {
      clip-path: polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%);
    }
  }

  &__content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 2;
    text-align: center;
    padding: 0 24px;
    pointer-events: none;
    background: transparent;

    &--left {
      clip-path: polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%);
    }

    &--right {
      display: flex;
      clip-path: polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%);
    }
  }

  &__mask {
    display: block;
  }

  // Keep the mobile video inside the pinned viewport without cropping its aspect ratio.
  &__video {
    display: flex;
    align-items: center;
    justify-content: center;

    .e-adaptive-media__video,
    .e-video-player {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .e-adaptive-media__video video,
    .e-video-player video {
      width: 100% !important;
      height: 100% !important;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain !important;
    }

    @include tablet {
      .e-adaptive-media__video video,
      .e-video-player video {
        object-fit: cover !important;
      }
    }
  }

  @include tablet {
    min-height: 100vh;
    height: 100vh;
    padding: 40px 0;

    &__video {
      position: absolute;
      inset: 0;
      height: 100%;

      &.e-adaptive-media,
      .e-adaptive-media__video,
      .e-video-player,
      video {
        height: 100%;
      }

      .e-video-player video,
      video {
        object-fit: cover;
      }
    }

    &__mask {
      display: block;
    }

    &__content {
      position: absolute;
      inset: 0;
      z-index: 2;
      padding: 0 24px;
      background: transparent;

      &--left {
        clip-path: polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%);
      }

      &--right {
        display: flex;
        clip-path: polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%);
      }
    }
  }

  &__title {
    font-family: $font-family;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: $black;
    max-width: 800px;
    margin: 0;

    p {
      margin: 0;
    }
  }

  &__divider {
    width: 24px;
    height: 8px;
    background: $color-growth-green;
    transform: skewX(-20deg);
    margin: 24px auto;
  }

  &__desc {
    font-family: $font-family;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    color: rgba(0, 0, 0, 0.85);
    max-width: 800px;
    margin: 0;

    p {
      margin: 0;
    }
  }

  @include tablet {
    &__title {
      font-size: 48px;
      line-height: 1.15;
    }

    &__desc {
      font-size: 18px;
      line-height: 1.5;
    }
  }

  @include desktop {
    &__title {
      font-size: 56px;
    }

    &__title,
    &__desc {
      max-width: 900px;
    }
  }
}
</style>
